import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  tone = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();

  const isDanger = tone === 'danger';
  const accent = isDanger ? 'rgba(220,53,69,' : 'rgba(217,178,86,';

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return createPortal(
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-6)',
        backgroundColor: 'rgba(17,17,17,0.42)',
        backdropFilter: 'blur(4px)',
        zIndex: 2200,
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(100%, 440px)',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--color-surface)',
          border: `1px solid ${accent}0.22)`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-4)',
            padding: 'var(--spacing-5)',
            borderBottom: '1px solid var(--color-border-light)',
            background: `linear-gradient(180deg, ${accent}0.03) 0%, ${accent}0) 100%)`,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${accent}0.14)`,
              color: isDanger ? 'var(--color-error)' : '#8a6505',
              fontFamily: 'var(--font-family-heading)',
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            !
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2
              id={titleId}
              style={{ margin: 0, fontSize: 'var(--font-size-lg)', lineHeight: 1.2 }}
            >
              {title}
            </h2>
            <p
              id={descId}
              style={{
                marginTop: 'var(--spacing-2)',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-base)',
                lineHeight: 1.5,
              }}
            >
              {message}
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--spacing-3)',
            padding: 'var(--spacing-4) var(--spacing-5)',
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.65rem 1.2rem',
              borderRadius: '999px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'transparent',
              color: 'var(--color-text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '0.65rem 1.2rem',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: isDanger ? 'var(--color-error)' : 'var(--color-primary)',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
